import React from "react";
import MergeTable from "../../../../../../shared/MergeTable";
import { PendingInvite } from "../../../../../../../models/Entities";
import {
  Text,
  Menu,
  MenuItem,
  ButtonVariant,
  SkeletonLoader,
} from "@merge-api/merge-javascript-shared";
import { ReactComponent as FrownyFace } from "../../../../../../../../src/assets/svg/frowny-face.svg";
import useAppContext from "../../../../../../context/useAppContext";
import { fetchWithAuth } from "../../../../../../../api-client/APIClient";
import { showSuccessToast } from "../../../../../../shared/Toasts";
import { MoreVertical } from "lucide-react";

type PendingTableProps = {
  pendingInvites?: PendingInvite[];
  isLoading: boolean;
  onRefetchPendingUsers: () => void;
};

const PendingInvitesTable = ({
  pendingInvites = [],
  isLoading,
  onRefetchPendingUsers,
}: PendingTableProps) => {
  // HOOKS
  const { isUserPrivileged: isMeUserPrivileged } = useAppContext();

  // HANDLERS
  const deletePendingInvites = (pendingInvite: PendingInvite) => {
    fetchWithAuth({
      path: "/organizations/users/invites/" + pendingInvite.id,
      method: "DELETE",
      onResponse: () => {
        showSuccessToast("Successfully deleted pending invite for " + pendingInvite.email + "!");
        onRefetchPendingUsers();
      },
    });
  };

  return (
    <>
      {isLoading || pendingInvites.length > 0 ? (
        <MergeTable
          noBoxShadow
          hasMorePaddingOnFirstElement
          hover={false}
          header={
            <>
              <th scope="col">Email address</th>
            </>
          }
          content={
            isLoading
              ? Array.from({ length: 5 }).map((_, index) => (
                  <tr key={`skeleton-row-${index}`}>
                    <td colSpan={2}>
                      {/* 40 is the row's actions button, its tallest element, so the bar matches its height */}
                      <SkeletonLoader height={40} borderRadius={8} fullWidth />
                    </td>
                  </tr>
                ))
              : pendingInvites.map((pendingInvite) => (
                  <tr key={pendingInvite.id}>
                    {/* Pending invitee email is user PII and is thus masked at the cell */}
                    <td className="ph-no-capture">
                      <Text>{pendingInvite.email}</Text>
                    </td>
                    <td className="w-24">
                      {isMeUserPrivileged && (
                        <Menu
                          ButtonProps={{
                            children: <MoreVertical size={16} />,
                            variant: ButtonVariant.IconShadowHidden,
                            size: "md",
                          }}
                          menuPlacement="bottom-end"
                        >
                          <MenuItem
                            onClick={() => deletePendingInvites(pendingInvite)}
                            className="text-red-60"
                          >
                            Delete invite
                          </MenuItem>
                        </Menu>
                      )}
                    </td>
                  </tr>
                ))
          }
        />
      ) : (
        <MergeTable
          noBoxShadow
          hover={false}
          content={
            <div className="flex flex-col items-center gap-4 py-8">
              <FrownyFace />
              <Text className="text-gray-40" variant="h4">
                No pending invites
              </Text>
            </div>
          }
        />
      )}
    </>
  );
};

export default PendingInvitesTable;
